import { settings } from "app";
import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  DateField,
  Labeled,
  RecordContextProvider,
  SimpleShowLayout,
  useNotify,
  useRecordContext,
} from "react-admin";
import { Box, Grid, Typography, useMediaQuery } from "@mui/material";
import dataProvider from "server/dataProvider";
import { specialistRequests } from "api/specialistApi";
import { useParams } from "react-router";
import SecuredComponent from "server/security/SecuredComponent";
import permissions from "app/permissions";
import FeesHistoryDialog from "./FeesHistoryDialog";
import SpecializationsFeesEditForm from "./SpecializationsFeesEditForm";
import EditIcon from "@mui/icons-material/Edit";
import { MoneyField } from "server/components/Fields/MoneyField";
import { SpecializationTypes } from "utils/constants";

const SpecializationFeesSection = () => {
  const isMobile = useMediaQuery(theme => theme.breakpoints.down("md"));
  const { id } = useParams();
  // Specialization record from <Show>; read before the fees RecordContextProvider overrides it
  const specialization = useRecordContext();
  const isClinical = specialization?.type === SpecializationTypes.Clinical;
  const [isEditing, setIsEditing] = useState(false);
  const notify = useNotify();

  const [specializationFees, setSpecializationFees] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);

  const fetchFees = useCallback(async () => {
    setLoadFailed(false);
    try {
      const { data } = await dataProvider.customRequest(specialistRequests.getSpecializationFees(id));
      setSpecializationFees(data);
    } catch (err) {
      setSpecializationFees(null);
      if (err?.status !== 404) {
        setLoadFailed(true);
        notify("Неуспешно зареждане на таксите. Опитайте да презаредите страницата.", { type: "error" });
      }
    } finally {
      setLoading(false);
    }
  }, [id, notify]);

  useEffect(() => {
    if (!id) return;
    fetchFees();
  }, [id, fetchFees]);

  return (
    <SimpleShowLayout>
      <RecordContextProvider value={specializationFees}>
        <Grid
          container
          spacing={2}
          sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}
        >
          {isEditing ? (
            <SpecializationsFeesEditForm
              id={id}
              specializationType={specialization?.type}
              setIsEditing={setIsEditing}
              fetchFees={fetchFees}
            />
          ) : loadFailed ? (
            <Grid
              item
              xs={12}
            >
              <Typography color="error">Неуспешно зареждане на таксите.</Typography>
            </Grid>
          ) : (
            <>
              {!loading && !specializationFees ? (
                <Grid
                  item
                  xs={12}
                >
                  <Typography>Няма въведени такси.</Typography>
                </Grid>
              ) : (
                <>
                  {!isClinical && (
                    <>
                      <Grid
                        item
                        xs={12}
                        md={4}
                      >
                        <Labeled label="Месечна такса за практическо обучение">
                          <MoneyField
                            source="practicalTrainingMonthlyFee"
                            emptyText="-"
                          />
                        </Labeled>
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        md={4}
                      >
                        <Labeled label="Годишна такса за практическо обучение">
                          <MoneyField
                            source="practicalTrainingAnnualFee"
                            emptyText="-"
                          />
                        </Labeled>
                      </Grid>
                    </>
                  )}
                  <Grid
                    item
                    xs={12}
                    md={4}
                  >
                    <Labeled label="Месечна такса за теоретично обучение">
                      <MoneyField
                        source="theoreticalTrainingMonthlyFee"
                        emptyText="-"
                      />
                    </Labeled>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={4}
                  >
                    <Labeled label="Годишна такса за теоретично обучение">
                      <MoneyField
                        source="theoreticalTrainingAnnualFee"
                        emptyText="-"
                      />
                    </Labeled>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={4}
                  >
                    <Labeled label="Такса за държавен изпит">
                      <MoneyField
                        source="stateExamFee"
                        emptyText="-"
                      />
                    </Labeled>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={4}
                  >
                    <Labeled label="Създадени на">
                      <DateField
                        source="createdOn"
                        locales="bg-BG"
                      />
                    </Labeled>
                  </Grid>
                </>
              )}
              <Box>
                <SecuredComponent permission={permissions.canManageSpecializationFees}>
                  <Button
                    variant="contained"
                    startIcon={<EditIcon />}
                    label={specializationFees ? "Редактирай" : "Добави"}
                    onClick={() => setIsEditing(v => !v)}
                    sx={{ m: 2 }}
                  />
                </SecuredComponent>
                {specializationFees && (
                  <SecuredComponent permission={permissions.canPreviewSpecializationFees}>
                    <FeesHistoryDialog id={id} />
                  </SecuredComponent>
                )}
              </Box>
            </>
          )}
        </Grid>
      </RecordContextProvider>
    </SimpleShowLayout>
  );
};

export default SpecializationFeesSection;
